import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Trophy, Medal, Coins, Music, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getRankIcon = (rank) => {
  if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
  if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
  return <span className="w-6 h-6 flex items-center justify-center text-gray-400 font-bold">{rank}</span>;
};

const getRankBg = (rank) => {
  if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-500/30';
  if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/20 border-gray-400/30';
  if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-600/30';
  return 'bg-white/5 border-white/10';
};

export default function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/leaderboard`);
      setLeaderboard(res.data);
    } catch (error) {
      logger.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="leaderboard-page">
      <div className="container py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Fan Leaderboard</h1>
          <p className="text-gray-400">Top token holders in the @DaRealQJones community</p>
        </div>

        {/* Stats Summary */}
        {leaderboard.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="glass-card p-4 text-center">
              <Coins className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-2xl font-bold">{leaderboard.reduce((sum, u) => sum + u.total_tokens, 0)}%</p>
              <p className="text-xs text-gray-400">Total Tokens Held</p>
            </div>
            <div className="glass-card p-4 text-center">
              <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold">${leaderboard.reduce((sum, u) => sum + u.total_value, 0).toFixed(0)}</p>
              <p className="text-xs text-gray-400">Total Invested</p>
            </div>
            <div className="glass-card p-4 text-center">
              <Music className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold">{leaderboard.length}</p>
              <p className="text-xs text-gray-400">Token Holders</p>
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {leaderboard.length === 0 ? (
          <div className="empty-state glass-card py-16">
            <Trophy className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">No token holders yet</h3>
            <p className="text-gray-400">Be the first to own a piece of the music!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className="grid grid-cols-3 gap-4 mb-8">
                {/* 2nd Place */}
                <div className="glass-card p-6 text-center mt-8 border border-gray-400/30">
                  <div className="w-12 h-12 rounded-full bg-gray-400/20 flex items-center justify-center mx-auto mb-3">
                    <Medal className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="font-bold truncate">{leaderboard[1]?.name}</p>
                  <p className="text-2xl font-bold text-gray-300">{leaderboard[1]?.total_tokens}%</p>
                  <p className="text-xs text-gray-400">{leaderboard[1]?.tracks_owned} tracks</p>
                </div>
                
                {/* 1st Place */}
                <div className="glass-card p-6 text-center border-2 border-yellow-500/50 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-yellow-500 rounded-full text-xs font-bold text-black">
                    #1
                  </div>
                  <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-7 h-7 text-yellow-400" />
                  </div>
                  <p className="font-bold truncate">{leaderboard[0]?.name}</p>
                  <p className="text-3xl font-bold text-yellow-400">{leaderboard[0]?.total_tokens}%</p>
                  <p className="text-sm text-gray-400">{leaderboard[0]?.tracks_owned} tracks</p>
                  <p className="text-sm text-green-400 mt-1">${leaderboard[0]?.total_value}</p>
                </div>
                
                {/* 3rd Place */}
                <div className="glass-card p-6 text-center mt-12 border border-amber-600/30">
                  <div className="w-12 h-12 rounded-full bg-amber-600/20 flex items-center justify-center mx-auto mb-3">
                    <Medal className="w-6 h-6 text-amber-600" />
                  </div>
                  <p className="font-bold truncate">{leaderboard[2]?.name}</p>
                  <p className="text-2xl font-bold text-amber-500">{leaderboard[2]?.total_tokens}%</p>
                  <p className="text-xs text-gray-400">{leaderboard[2]?.tracks_owned} tracks</p>
                </div>
              </div>
            )}

            {/* Full List */}
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-semibold">All Rankings</h3>
              </div>
              <div className="divide-y divide-white/5">
                {leaderboard.map((entry) => (
                  <div 
                    key={entry.user_id}
                    className={`flex items-center gap-4 p-4 ${getRankBg(entry.rank)} border-l-4`}
                    data-testid={`leaderboard-entry-${entry.rank}`}
                  >
                    <div className="w-10 flex justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{entry.name}</p>
                      <p className="text-sm text-gray-400">{entry.tracks_owned} track{entry.tracks_owned !== 1 ? 's' : ''} owned</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{entry.total_tokens}%</p>
                      <p className="text-sm text-green-400">${entry.total_value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
